/**
 * The top header of a CS scratchpad
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import type {SingleBreadcrumb} from "#components/breadcrumbs-package/deprecated-breadcrumbs.tsx";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
import type {
    Scratchpad,
    UserScratchpad,
} from "#scratchpad-base-package/scratchpads.ts";
import ChallengePane from "#scratchpad-challenge-package/challenge-pane.tsx";
import type {ScratchpadChallenge} from "#scratchpad-challenge-package/scratchpad-challenge.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";
import ContentPageHeader from "#tutorial-shared-package/components/content-page-header.tsx";

import type {ScratchpadTopic} from "./internal/types.ts";

type EditableTitleProps = {
    // Can the current user edit this scratchpad and are we in edit mode?
    inEditModeAndCanEdit: boolean;
    // Can this scratchpad be saved?
    isSaveable: boolean;
    // Trigger saving of the scratchpad
    onSave: (saveSettings: boolean) => unknown;
    // The scratchpad Backbone model which is being rendered
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad;
    // Set the title using a promise
    setTitle: (title: string) => Promise<unknown>;
    // The topic in which this scratchpad is being displayed
    topic: ScratchpadTopic;
    translatedTitle: string;
};

type EditableTitleState = {
    newTitle: string;
};

class _EditableTitle extends React.Component<
    EditableTitleProps,
    EditableTitleState
> {
    render(): React.ReactNode {
        const title = this.props.scratchpad.get("translatedTitle");

        if (!this.props.inEditModeAndCanEdit || !this.props.isSaveable) {
            return <span>{title}</span>;
        }

        const editTitleModal = ({closeModal}: {closeModal: () => void}) => (
            <OnePaneDialog
                title={t`Edit title`}
                content={
                    <View>
                        <Body>
                            <input
                                type="text"
                                defaultValue={this.props.scratchpad.get(
                                    "translatedTitle",
                                )}
                                className={css(styles.titleInput)}
                                onChange={(e) =>
                                    this.setState({newTitle: e.target.value})
                                }
                            />
                        </Body>
                    </View>
                }
                footer={
                    <View style={styles.modalFooter}>
                        <Button kind="tertiary" onClick={closeModal}>
                            {t`Cancel`}
                        </Button>
                        <Strut size={16} />
                        <Button
                            onClick={() => {
                                this.props
                                    .setTitle(this.state.newTitle)
                                    .then(() => {
                                        this.props.onSave(false);
                                        closeModal();
                                    });
                            }}
                        >
                            {t`Save`}
                        </Button>
                    </View>
                }
                style={styles.dialog}
            />
        );

        return (
            <View style={styles.titleWrap}>
                <span>{title}</span>
                <Strut size={16} />
                <View className={css(styles.editTitle)}>
                    <ModalLauncher modal={editTitleModal}>
                        {({openModal}) => (
                            <Button kind="tertiary" onClick={openModal}>
                                {t`Edit title`}
                            </Button>
                        )}
                    </ModalLauncher>
                </View>
            </View>
        );
    }
}

const EditableTitle = withBackboneModels((props: EditableTitleProps) => [
    props.scratchpad,
])(_EditableTitle);

type ScratchpadHeaderProps = {
    // The challenge that might be happening on this page
    // @ts-expect-error - TS2749 - 'ScratchpadChallenge' refers to a value, but is being used as a type here. Did you mean 'typeof ScratchpadChallenge'?
    challenge: ScratchpadChallenge | null | undefined;
    // Is this scratchpad embedded in another page (either via an iframe
    // or as inside a tutorial)
    embedded: boolean;
    // Is this particular scratchpad a challenge
    isChallenge: boolean;
    // Is this scratchpad documentation
    isDocumentation: boolean;
    // Is this scratchpad officially-created one (by KA staff)
    isOfficial: boolean;
    // Is the current user the owner of this scratchpad
    isOwner: boolean;
    // Is this a project fork that's unable to be edited
    isUneditableProjectFork: boolean;
    // A callback to trigger a spin-off
    onTriggerSpinoff: () => unknown;
    // The scratchpad Backbone model which is being rendered
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    originScratchpad: Scratchpad | null | undefined;
    // The root URL for the scratchpad creator's profile
    profileRoot: string;
    // The scratchpad Backbone model which is being rendered
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad;
    // Determine if the description of the scratchpad should be displayed
    showDescription: boolean;
    // The topic in which this scratchpad is being displayed
    topic: ScratchpadTopic;
    // The UserScratchpad in use on the project or challenge
    // @ts-expect-error - TS2749 - 'UserScratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof UserScratchpad'?
    userScratchpad: UserScratchpad;
    // Can the current user edit this scratchpad and are we in edit mode?
    inEditModeAndCanEdit: boolean;
    // Can this scratchpad be saved?
    isSaveable: boolean;
    // Trigger saving of the scratchpad
    onSave: (saveSettings: boolean) => unknown;
    // Set the title using a promise
    setTitle: (title: string) => Promise<unknown>;
    translatedTitle: string;
};

class _ScratchpadHeader extends React.Component<ScratchpadHeaderProps> {
    static defaultProps: {
        inEditModeAndCanEdit: boolean;
        profileRoot: string;
    } = {
        profileRoot: "",
        inEditModeAndCanEdit: false,
    };

    renderContentHeader(): React.ReactNode {
        const {
            topic,
            scratchpad,
            isDocumentation,
            isOwner,
            isOfficial,
            originScratchpad,
        } = this.props;

        const breadcrumbs: Array<SingleBreadcrumb> = [];

        if (topic.translatedTitle) {
            breadcrumbs.push({
                title: topic.translatedTitle,
                href: topic.relativeUrl,
            });
        }

        if (originScratchpad && !isOfficial) {
            breadcrumbs.push({
                title: originScratchpad.get("translatedTitle"),
                href: originScratchpad.get("deleted")
                    ? "javascript: void 0"
                    : originScratchpad.get("url"),
                forceServer: true,
            });
        }

        if (isDocumentation) {
            breadcrumbs.push({
                title: t`Documentation`,
                href: scratchpad.get("docsUrlPath"),
            });
        } else if (isOwner) {
            breadcrumbs.push({
                title: t`My Projects`,
                href: `${this.props.profileRoot}projects`,
                forceServer: true,
            });
        }
        const {
            inEditModeAndCanEdit,
            isSaveable,
            onSave,
            setTitle,
            translatedTitle,
        } = this.props;

        return (
            <ContentPageHeader
                title={
                    <EditableTitle
                        inEditModeAndCanEdit={inEditModeAndCanEdit}
                        isSaveable={isSaveable}
                        onSave={onSave}
                        scratchpad={scratchpad}
                        setTitle={setTitle}
                        topic={topic}
                        translatedTitle={translatedTitle}
                    />
                }
                breadcrumbs={breadcrumbs}
            />
        );
    }

    renderChallengePane(): null | React.ReactElement<
        React.ComponentProps<"div">
    > {
        const {challenge} = this.props;

        if (!challenge) {
            return null;
        }

        return (
            <div>
                <ChallengePane
                    challenge={challenge}
                    translatedTitle={this.props.scratchpad.get(
                        "translatedTitle",
                    )}
                />
            </div>
        );
    }

    render(): React.ReactNode {
        return (
            <div>
                {this.renderContentHeader()}
                {this.renderChallengePane()}
            </div>
        );
    }
}

const ScratchpadHeader: React.ComponentType<
    JSX.LibraryManagedAttributes<
        typeof _ScratchpadHeader,
        React.ComponentProps<typeof _ScratchpadHeader>
    >
> = withBackboneModels((props: ScratchpadHeaderProps) => {
    const models = [props.scratchpad];

    if (props.originScratchpad) {
        models.push(props.originScratchpad);
    }

    if (props.challenge) {
        models.push(props.challenge);
    }

    return models;
})(_ScratchpadHeader);

const styles = StyleSheet.create({
    dialog: {
        [mediaQueries.mdOrLarger]: {
            height: "auto",
        },
    },

    titleWrap: {
        flexDirection: "row",
        position: "relative",
    },

    editTitle: {
        bottom: 0,
        left: "100%",
        position: "absolute",
        whiteSpace: "nowrap",
    },

    titleInput: {
        ...deprecatedTypography.bodySmall,
        width: "100%",
    },

    modalFooter: {
        flexDirection: "row",
    },
});

export default ScratchpadHeader;
